<div class="container">

  <header class="page-heading">
    <h1>EPF Calculator</h1>
    <p>Calculate your EPF (Employee Provident Fund) maturity value at retirement, including employee and employer contributions.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">EPF Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('currentAge')">
        <label class="field-label" for="currentAge">Current Age</label>
        <div class="input-shell suffix">
          <input id="currentAge" type="number"
            [(ngModel)]="currentAge"
            [min]="C.age.min" [max]="C.age.max" step="1"
            (ngModelChange)="onFieldChange('currentAge')"
            (blur)="onFieldBlur('currentAge')"
            placeholder="e.g. 28"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('currentAge')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('currentAge') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('retirementAge')">
        <label class="field-label" for="retirementAge">Retirement Age</label>
        <div class="input-shell suffix">
          <input id="retirementAge" type="number"
            [(ngModel)]="retirementAge"
            [min]="C.age.min" max="60" step="1"
            (ngModelChange)="onFieldChange('retirementAge')"
            (blur)="onFieldBlur('retirementAge')"
            placeholder="e.g. 58"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('retirementAge')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('retirementAge') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('monthlyBasicSalary')">
        <label class="field-label" for="monthlyBasicSalary">Monthly Basic + DA</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyBasicSalary" type="number"
            [(ngModel)]="monthlyBasicSalary"
            [min]="C.basicSalary.min" [max]="C.basicSalary.max" [step]="C.basicSalary.step"
            (ngModelChange)="onFieldChange('monthlyBasicSalary')"
            (blur)="onFieldBlur('monthlyBasicSalary')"
            placeholder="e.g. 30,000"
            autocomplete="off"/>
        </div>
        @if (hasError('monthlyBasicSalary')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlyBasicSalary') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('salaryGrowthPercent')">
        <label class="field-label" for="salaryGrowthPercent">Annual Salary Growth</label>
        <div class="input-shell suffix">
          <input id="salaryGrowthPercent" type="number"
            [(ngModel)]="salaryGrowthPercent"
            [min]="C.salaryGrowth.min" [max]="C.salaryGrowth.max" [step]="C.salaryGrowth.step"
            (ngModelChange)="onFieldChange('salaryGrowthPercent')"
            (blur)="onFieldBlur('salaryGrowthPercent')"
            placeholder="e.g. 8"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('salaryGrowthPercent')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('salaryGrowthPercent') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('employeePercent')">
        <label class="field-label" for="employeePercent">Employee Contribution</label>
        <div class="input-shell suffix">
          <input id="employeePercent" type="number"
            [(ngModel)]="employeePercent"
            [min]="C.employeePercent.min" [max]="C.employeePercent.max" [step]="C.employeePercent.step"
            (ngModelChange)="onFieldChange('employeePercent')"
            (blur)="onFieldBlur('employeePercent')"
            placeholder="e.g. 12"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('employeePercent')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('employeePercent') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('employerPercent')">
        <label class="field-label" for="employerPercent">Employer Contribution (EPF)</label>
        <div class="input-shell suffix">
          <input id="employerPercent" type="number"
            [(ngModel)]="employerPercent"
            [min]="C.employerPercent.min" [max]="C.employerPercent.max" [step]="C.employerPercent.step"
            (ngModelChange)="onFieldChange('employerPercent')"
            (blur)="onFieldBlur('employerPercent')"
            placeholder="e.g. 3.67"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">Default 3.67% &mdash; the rest of the employer's 12% goes to EPS.</div>
        @if (hasError('employerPercent')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('employerPercent') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('annualInterestRate')">
        <label class="field-label" for="annualInterestRate">EPF Interest Rate</label>
        <div class="input-shell suffix">
          <input id="annualInterestRate" type="number"
            [(ngModel)]="annualInterestRate"
            [min]="C.interestRate.min" [max]="C.interestRate.max" [step]="C.interestRate.step"
            (ngModelChange)="onFieldChange('annualInterestRate')"
            (blur)="onFieldBlur('annualInterestRate')"
            placeholder="e.g. 8.25"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualInterestRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualInterestRate') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('currentBalance')">
        <label class="field-label" for="currentBalance">Current EPF Balance</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="currentBalance" type="number"
            [(ngModel)]="currentBalance"
            [min]="C.currentBalance.min" [max]="C.currentBalance.max" [step]="C.currentBalance.step"
            (ngModelChange)="onFieldChange('currentBalance')"
            (blur)="onFieldBlur('currentBalance')"
            placeholder="e.g. 0"
            autocomplete="off"/>
        </div>
        @if (hasError('currentBalance')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('currentBalance') }}
          </div>
        }
      </div>
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">EPF Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">at age {{ retirementAge }} ({{ tenureYears }} years)</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Contribution</div>
        <div class="sc-value">{{ fmt.currency(result.totalEmployeeContribution + result.totalEmployerContribution) }}</div>
        <div class="sc-sub">employee + employer</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Interest Earned</div>
        <div class="sc-value">{{ fmt.currency(result.totalInterest) }}</div>
        <div class="sc-sub">@ {{ annualInterestRate }}% p.a.</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Contribution vs Interest</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Balance Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-wise Growth</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Age</th>
              <th>Monthly Basic</th>
              <th class="t-principal">Employee</th>
              <th class="t-interest">Employer</th>
              <th class="t-balance">Balance</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.years; track row.year) {
              <tr>
                <td class="t-left t-year">{{ row.age }}</td>
                <td>{{ fmt.currency(row.monthlyBasic) }}</td>
                <td class="t-principal">{{ fmt.currency(row.employeeContribution) }}</td>
                <td class="t-interest">{{ fmt.currency(row.employerContribution) }}</td>
                <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is EPF Maturity Calculated?</h2>
    <p>
      The Employees' Provident Fund (EPF) is a retirement savings scheme where both you and your
      employer contribute a percentage of your basic salary + DA every month. Of the employer's 12%
      contribution, 8.33% (capped) goes to the Employee Pension Scheme (EPS) and the remaining
      3.67% goes into your EPF account, which earns interest declared annually by EPFO.
    </p>
    <p>
      This calculator assumes your basic salary grows every year, and compounds employee + employer
      contributions monthly at the EPF interest rate until your retirement age.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Is EPF interest taxable?</h3>
      <p>
        EPF interest is tax-free up to a specified annual contribution threshold; interest on
        employee contributions above that threshold is taxable. Withdrawals after 5 years of
        continuous service are generally tax-free.
      </p>
    </div>
    <div class="faq-item">
      <h3>Can I contribute more than 12% to my EPF?</h3>
      <p>
        Yes, through the Voluntary Provident Fund (VPF), you can contribute up to 100% of your
        basic salary + DA, and it earns the same interest rate as your regular EPF contribution.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/nps-vs-epf">NPS vs EPF: which retirement account wins?</a>
    </p>
  </div>
</section>
